import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'signup' | 'reset'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const { signIn, signUp, resetPassword } = useAuth();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      if (mode === 'login') {
        await signIn(email, password);
        setMessage('Login realizado com sucesso!');
        setTimeout(() => onClose(), 1500);
      } else if (mode === 'signup') {
        await signUp(email, password, fullName);
        setMessage('Cadastro realizado! Verifique seu e-mail.');
        setTimeout(() => {
          setMode('login');
          setMessage('');
        }, 2000);
      } else if (mode === 'reset') {
        await resetPassword(email);
        setMessage('E-mail de recuperação enviado!');
        setTimeout(() => {
          setMode('login');
          setMessage('');
        }, 2000);
      }
    } catch (error: any) {
      setMessage(error.message || 'Ocorreu um erro. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl max-w-md w-full p-8 border border-gray-700 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white cursor-pointer"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        <div className="text-center mb-8">
          <h2 className="text-3xl font-black text-white mb-2">
            {mode === 'login' && 'Bem-vindo de Volta!'}
            {mode === 'signup' && 'Criar Conta'}
            {mode === 'reset' && 'Recuperar Senha'}
          </h2>
          <p className="text-gray-400">
            {mode === 'login' && 'Entre para continuar suas compras'}
            {mode === 'signup' && 'Cadastre-se para começar'}
            {mode === 'reset' && 'Digite seu e-mail para recuperar'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'signup' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nome Completo
              </label>
              <input
                type="text"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-neon-cyan focus:outline-none text-sm"
                placeholder="Seu nome completo"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              E-mail
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-neon-cyan focus:outline-none text-sm"
              placeholder="seu@email.com"
            />
          </div>

          {mode !== 'reset' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Senha
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-neon-cyan focus:outline-none text-sm"
                placeholder="••••••••"
              />
            </div>
          )}

          {message && (
            <div className={`p-3 rounded-lg text-sm ${
              message.includes('sucesso') || message.includes('enviado')
                ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                : 'bg-red-500/20 text-red-400 border border-red-500/50'
            }`}>
              {message}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-pink-600 to-cyan-600 hover:from-pink-500 hover:to-cyan-500 text-white py-3 rounded-lg font-bold transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
          >
            {loading ? (
              <i className="ri-loader-4-line animate-spin"></i>
            ) : (
              <>
                {mode === 'login' && 'Entrar'}
                {mode === 'signup' && 'Criar Conta'}
                {mode === 'reset' && 'Enviar E-mail'}
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center space-y-2">
          {mode === 'login' && (
            <>
              <button
                onClick={() => setMode('reset')}
                className="text-neon-cyan hover:underline text-sm cursor-pointer"
              >
                Esqueceu sua senha?
              </button>
              <p className="text-gray-400 text-sm">
                Não tem conta?{' '}
                <button
                  onClick={() => setMode('signup')}
                  className="text-neon-cyan hover:underline cursor-pointer"
                >
                  Cadastre-se
                </button>
              </p>
            </>
          )}

          {(mode === 'signup' || mode === 'reset') && (
            <p className="text-gray-400 text-sm">
              Já tem conta?{' '}
              <button
                onClick={() => setMode('login')}
                className="text-neon-cyan hover:underline cursor-pointer"
              >
                Fazer login
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
